import DeleteButton from '@pinecast/common/DeleteButton';
import type {ColorMap, CSS} from '@pinecast/styles';
import styled, {useColors} from '@pinecast/styles';
import * as React from 'react';

type Props = {
  $borderless: boolean;
  $color: 'gray' | 'green' | 'blue' | 'red' | 'yellow';
  $hasDeleteButton: boolean;
  $size: 'small' | 'medium' | 'large';
};

// Everything is `box-sizing: border-box`, so dropping the 1px border leaves the
// height alone but pulls the text 1px closer to each edge; giving the horizontal
// padding that pixel back keeps a borderless tag exactly the size of a bordered
// one, so the two can sit side by side without the row shifting.
const BORDER_WIDTH = 1;

const bgColors = (designColors: ColorMap) => ({
  gray: designColors.badgeGrayBackground,
  green: designColors.badgeGreenBackground,
  blue: designColors.badgeBlueBackground,
  red: designColors.badgeRedBackground,
  yellow: designColors.badgeYellowBackground,
});

const idleColors = (designColors: ColorMap) => ({
  gray: designColors.badgeGrayText,
  green: designColors.badgeGreenText,
  blue: designColors.badgeBlueText,
  red: designColors.badgeRedText,
  yellow: designColors.badgeYellowText,
});

const Tag_ = styled(
  'span',
  ({$borderless, $color, $hasDeleteButton, $size}: Props, designColors) => {
    const reclaimed = $borderless ? BORDER_WIDTH : 0;
    return {
      alignItems: 'center',
      alignSelf: 'center',
      backgroundColor: bgColors(designColors)[$color],
      borderRadius: $size === 'large' ? 5 : 4,
      border: $borderless
        ? 0
        : `${BORDER_WIDTH}px solid ${idleColors(designColors)[$color]}`,
      color: idleColors(designColors)[$color],
      display: 'inline-flex',
      flex: '0 0 0%',
      fontSize: $size === 'small' ? 11 : $size === 'medium' ? 13 : 16,
      fontWeight: 400,
      height: $size === 'small' ? 16 : $size === 'medium' ? 20 : 24,
      paddingTop: 0,
      paddingLeft: 6 + reclaimed,
      paddingRight:
        ($hasDeleteButton ? ($size === 'large' ? 2 : 1) : 6) + reclaimed,
      paddingBottom: 0,
      transition: 'background-color 0.2s, color 0.2s',
      whiteSpace: 'nowrap',
    };
  },
  {className: 'Tag'},
);

const DBLabel = styled('span', {
  display: 'inline-block',
});

type ExternalProps = {
  // Drops the border without changing the tag's footprint. Reach for this when
  // the same tags repeat down a vertical run — a list of reviews, a table
  // column, a feed of rows — where a bordered pill on every line stacks up into
  // a column of outlines that competes with the content for attention. The fill
  // still carries the color, so nothing is lost but the noise. A tag that
  // appears once, or one that has to read as an interactive chip (anything with
  // `deleteButton`), should keep its border.
  $borderless?: boolean;
  children: React.ReactNode;
  color?: Props['$color'];
  deleteButton?: boolean;
  onDelete?: () => void;
  size?: Props['$size'];
  style?: CSS;
};

const Tag = (props: ExternalProps) => {
  const designColors = useColors();

  if (props.deleteButton && !props.onDelete) {
    throw new Error('Created Tag with deleteButton but not onDelete');
  }
  const dbSize = props.size === 'small' ? 16 : 20;
  return (
    <Tag_
      $borderless={props.$borderless || false}
      $color={props.color || 'gray'}
      $hasDeleteButton={props.deleteButton || false}
      $size={props.size || 'medium'}
      style={props.style}
    >
      {props.deleteButton ? (
        <React.Fragment>
          <DBLabel>{props.children}</DBLabel>
          <DeleteButton
            idleColor={idleColors(designColors)[props.color || 'gray']}
            onClick={props.onDelete as () => void}
            style={{
              alignSelf: 'center',
              height: dbSize,
              marginTop: 0,
              marginLeft: props.size === 'large' ? undefined : -2,
              width: dbSize,
            }}
          />
        </React.Fragment>
      ) : (
        props.children
      )}
    </Tag_>
  );
};

export default Tag;
